import type { OrderSummaryResponse } from '@thinkalpha/locates-ws-client/contracts/orders.js';
import { log } from 'src/admin/LAB/log';
import type { RevenueAnalyticsRawRow } from 'src/admin/LAB/RevenueAnalytics/types';
import { lapi as axios } from 'src/api';
import { escapeODataValue } from 'src/lib/oData/escapeODataValue';

export interface GetRevenueAnalyticsOptions {
    /**
     * Limit the rows to these providers. Omitted (or empty) loads the
     * aggregate across every provider the caller can see.
     */
    providerIds?: ReadonlyArray<string>;
    /**
     * Additional OData `$filter` predicate over the numeric row fields
     * (`quantity`, `averagePrice`, `totalCost`, `currentPrice`), and-ed with
     * the providers clause, e.g. `(quantity gt 100) and (totalCost le 5000)`.
     */
    filter?: string;
    /** Zero-based starting row, sent as OData `$skip`. Omitted (with `top`) when searching a symbol. */
    skip?: number;
    /** Page size, sent as OData `$top`. Omitted (with `skip`) when searching a symbol. */
    top?: number;
    /**
     * OData `$orderby` expression over the row shape (`providerId`,
     * `symbol`, `quantity`, `averagePrice`, `totalCost`, `currentPrice`),
     * e.g. `totalCost desc`. Omitted uses the backend default sort
     * (provider, then cost descending).
     */
    orderby?: string;
    /**
     * Exact-match symbol filter, sent as a `symbol eq …` `$filter` clause
     * and-ed with the others. Callers omit paging with it: a symbol yields
     * at most one row per provider.
     */
    symbol?: string;
}

/**
 * Fetch one page of per-symbol revenue analytics and map the response
 * onto the table's raw row shape. Each row keeps its `providerId`; the
 * display name is resolved later against the loaded providers list, since
 * the aggregate view spans providers.
 *
 * Served by `GET /v1/orders/summary/by-symbol`, driven entirely by OData
 * over the row shape: the provider selection (`providerId eq …`, or-ed
 * across several providers), the symbol search (`symbol eq …`), and the
 * column `filter` are and-ed into one `$filter`; sorting is `$orderby`, and
 * paging `$skip`/`$top`. `inventory` is
 * optional per row, so the SOD figures are left `null` when the backend
 * has no start-of-day data (the table renders those as a dash).
 */
export const getRevenueAnalytics = async (
    options: GetRevenueAnalyticsOptions = {},
): Promise<RevenueAnalyticsRawRow[]> => {
    const { providerIds = [], filter, skip, top, orderby, symbol } = options;
    const providerClause =
        providerIds.length === 0
            ? undefined
            : providerIds.map((id) => `providerId eq '${escapeODataValue(id)}'`).join(' or ');
    const symbolClause = symbol ? `symbol eq '${escapeODataValue(symbol)}'` : undefined;
    const clauses = [providerClause, symbolClause, filter].filter((clause): clause is string => Boolean(clause));
    if (TRACE) {
        log.trace(`Revenue analytics request: ${providerIds.length} provider(s), symbol ${symbol ? 'set' : 'unset'}`, {
            providerCount: providerIds.length,
            hasSymbol: Boolean(symbol),
            hasColumnFilter: Boolean(filter),
            skip,
            top,
        });
    }
    const { data } = await axios.get<OrderSummaryResponse>('/v1/orders/summary/by-symbol', {
        params: {
            $filter:
                clauses.length === 0
                    ? undefined
                    : clauses.length === 1
                      ? clauses[0]
                      : clauses.map((clause) => `(${clause})`).join(' and '),
            $orderby: orderby,
            $skip: skip,
            $top: top,
        },
    });
    return data.rows.map((row) => ({
        providerId: row.providerId,
        symbol: row.symbol,
        qty: row.quantity,
        avgPrice: row.averagePrice,
        totalCost: row.totalCost,
        curPrice: row.currentPrice,
        fairValue: row.fairValue,
        sodInventory: row.inventory?.startOfDay ?? null,
        remainingInventory: row.inventory?.remaining ?? null,
    }));
};
